import React from 'react';
import { Globe, Zap, Network, Radio, Shield, X } from 'lucide-react';
import { LocationData } from '../types';
import { CORE_SWITCH_TYPES, TRANSIT_PROVIDERS, IXP_EXCHANGES } from '../constants';

interface MmrViewProps {
  isOpen: boolean;
  onClose: () => void;
  location: LocationData;
  currentLocation: string;
  money: number;
  buildMmr: (locationId: string) => void;
  installCoreSwitch: (locationId: string, switchTypeId: string) => void;
  setShowMmrModal: (show: boolean) => void;
  setShowNetworkModal: (show: boolean) => void;
  formatCurrency: (amount: number, decimals?: number) => string;
}

const MMR_BUILD_COST = 25_000;

const SlotCard: React.FC<{
  label: string;
  icon: React.ReactNode;
  installed: boolean;
  content: React.ReactNode;
  action?: React.ReactNode;
}> = ({ label, icon, installed, content, action }) => (
  <div className={`rounded-lg border p-4 flex flex-col gap-2 ${installed ? 'bg-gray-800 border-gray-600' : 'bg-gray-900 border-dashed border-gray-700'}`}>
    <div className="flex items-center gap-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">
      {icon}
      {label}
    </div>
    <div className="flex-1 text-sm">{content}</div>
    {action && <div className="mt-1">{action}</div>}
  </div>
);

const MmrView: React.FC<MmrViewProps> = ({
  isOpen,
  onClose,
  location,
  currentLocation,
  money,
  buildMmr,
  installCoreSwitch,
  setShowMmrModal,
  setShowNetworkModal,
  formatCurrency,
}) => {
  if (!isOpen) return null;

  const mmr = location.mmr;

  const installedCore = mmr?.built && location.coreSwitch
    ? CORE_SWITCH_TYPES.find(t => t.id === location.coreSwitch!.typeId)
    : null;

  const activeTransit = (mmr?.transitProviders ?? []).map(tp => ({
    ...tp,
    provider: TRANSIT_PROVIDERS.find(p => p.id === tp.providerId),
  }));

  const activeIxp = (mmr?.ixpConnections ?? []).map(c => ({
    ...c,
    exchange: IXP_EXCHANGES.find(x => x.id === c.ixpId),
  }));

  const totalTransitGbps = activeTransit.reduce((s, t) => s + t.committedGbps, 0);
  const totalIxpGbps = activeIxp.reduce((s, c) => s + c.portSpeedGbps, 0);
  const totalExternalGbps = totalTransitGbps + totalIxpGbps;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-cyan-400" />
            <h2 className="text-base font-semibold">Meet-Me Room — {location.name}</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {!mmr?.built ? (
            /* ── Not built yet ── */
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Globe className="w-12 h-12 text-gray-600 mb-4" />
              <h3 className="text-lg font-semibold text-gray-300 mb-2">Not yet constructed</h3>
              <p className="text-sm text-gray-500 max-w-sm mb-6">
                The MMR is the physical room where carrier fiber terminates and your core network equipment is racked.
                Build it first — everything else (core switch, transit links, IXP) lives here.
              </p>
              <div className="text-sm text-gray-400 mb-4">
                Construction cost: <span className="text-white font-semibold">{formatCurrency(MMR_BUILD_COST)}</span>
              </div>
              <button
                onClick={() => { buildMmr(currentLocation); }}
                disabled={money < MMR_BUILD_COST}
                className="px-6 py-2.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold"
              >
                Build MMR
              </button>
              {money < MMR_BUILD_COST && (
                <p className="text-xs text-red-400 mt-2">Need {formatCurrency(MMR_BUILD_COST - money)} more</p>
              )}
            </div>
          ) : (
            /* ── Built ── */
            <div className="space-y-4">
              {/* Status bar */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-900/30 border border-emerald-700">
                <Globe className="w-4 h-4 text-emerald-400 shrink-0" />
                <span className="text-sm text-emerald-300 font-medium">MMR operational</span>
                <span className="text-xs text-gray-400 ml-auto">
                  {totalExternalGbps > 0
                    ? `${totalExternalGbps} Gbps external capacity`
                    : 'No external links — order transit to carry traffic'}
                </span>
                <button
                  onClick={() => { onClose(); setShowMmrModal(true); }}
                  className="text-xs px-3 py-1 rounded bg-cyan-800 hover:bg-cyan-700 text-cyan-200 shrink-0"
                >
                  Manage Connectivity
                </button>
              </div>

              {/* Equipment grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                {/* Core Switch */}
                <SlotCard
                  label="Core Switch"
                  icon={<Network className="w-3.5 h-3.5" />}
                  installed={!!installedCore}
                  content={
                    installedCore ? (
                      <div>
                        <div className="font-medium text-white">{installedCore.name}</div>
                        <div className="text-xs text-gray-400 mt-1">{installedCore.bandwidthGbps} Gbps fabric · {installedCore.powerDrawKW} kW</div>
                        <div className="text-xs text-gray-500 mt-1">{installedCore.description.split('.')[0]}</div>
                        {CORE_SWITCH_TYPES.filter(t => t.bandwidthGbps > installedCore.bandwidthGbps).length > 0 && (
                          <button
                            onClick={() => { onClose(); setShowNetworkModal(true); }}
                            className="mt-2 text-xs px-2 py-1 rounded bg-blue-900 hover:bg-blue-800 text-blue-300 border border-blue-700"
                          >
                            Upgrade available →
                          </button>
                        )}
                      </div>
                    ) : (
                      <div>
                        <div className="text-gray-500 text-xs mb-2">No core switch installed. Rack one here to aggregate floor uplinks.</div>
                        <div className="space-y-1">
                          {CORE_SWITCH_TYPES.map(t => (
                            <button
                              key={t.id}
                              onClick={() => installCoreSwitch(currentLocation, t.id)}
                              disabled={money < t.cost}
                              className="block w-full text-left text-xs px-2 py-1.5 rounded border transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-gray-800 border-gray-600 hover:border-blue-600 hover:bg-blue-900/30"
                            >
                              <span className="font-medium text-white">{t.name}</span>
                              <span className="text-gray-400 ml-2">{t.bandwidthGbps}G · {formatCurrency(t.cost)}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )
                  }
                />

                {/* ODF */}
                <SlotCard
                  label="Optical Distribution Frame"
                  icon={<Shield className="w-3.5 h-3.5" />}
                  installed={true}
                  content={
                    <div className="text-xs text-gray-400">
                      <div className="text-white font-medium mb-1">ODF Panel installed</div>
                      <div>Patch panel for fiber cross-connects between carrier cages and your core switch.</div>
                      <div className="mt-2 text-gray-500">
                        {activeTransit.length + activeIxp.length} active cross-connect{activeTransit.length + activeIxp.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  }
                />

                {/* Transit cross-connects */}
                <SlotCard
                  label="Transit Cross-Connects"
                  icon={<Zap className="w-3.5 h-3.5" />}
                  installed={activeTransit.length > 0}
                  content={
                    activeTransit.length > 0 ? (
                      <div className="space-y-2">
                        {activeTransit.map(t => (
                          <div key={t.providerId} className="flex items-center justify-between text-xs">
                            <div>
                              <span className="text-white font-medium">{t.provider?.name ?? t.providerId}</span>
                              <span className="text-gray-400 ml-2">{t.committedGbps} Gbps</span>
                            </div>
                            <span className="text-gray-500">{formatCurrency((t.provider?.costPerGbpsMonth ?? 0) * t.committedGbps)}/mo</span>
                          </div>
                        ))}
                        <div className="pt-1 border-t border-gray-700 text-xs text-gray-400">
                          Total: {totalTransitGbps} Gbps transit
                        </div>
                      </div>
                    ) : (
                      <div className="text-gray-500 text-xs">
                        No transit ordered. Order a transit provider to carry customer traffic to the internet.
                        <button
                          onClick={() => { onClose(); setShowMmrModal(true); }}
                          className="block mt-2 text-xs px-2 py-1 rounded bg-cyan-900 hover:bg-cyan-800 text-cyan-300 border border-cyan-700"
                        >
                          Order transit →
                        </button>
                      </div>
                    )
                  }
                />

                {/* IXP ports */}
                <SlotCard
                  label="IXP Port Connections"
                  icon={<Radio className="w-3.5 h-3.5" />}
                  installed={activeIxp.length > 0}
                  content={
                    activeIxp.length > 0 ? (
                      <div className="space-y-2">
                        {activeIxp.map(c => (
                          <div key={c.ixpId} className="flex items-center justify-between text-xs">
                            <div>
                              <span className="text-white font-medium">{c.exchange?.name ?? c.ixpId}</span>
                              <span className="text-gray-400 ml-2">{c.portSpeedGbps}G port</span>
                            </div>
                            <span className="text-emerald-400 text-xs">+{Math.round((c.exchange?.revenueBonus ?? 0) * 100)}% rev</span>
                          </div>
                        ))}
                        <div className="pt-1 border-t border-gray-700 text-xs text-gray-400">
                          Total: {totalIxpGbps} Gbps peering
                        </div>
                      </div>
                    ) : (
                      <div className="text-gray-500 text-xs">
                        No IXP connections. Requires ASN + PI IP space.
                        <button
                          onClick={() => { onClose(); setShowMmrModal(true); }}
                          className="block mt-2 text-xs px-2 py-1 rounded bg-cyan-900 hover:bg-cyan-800 text-cyan-300 border border-cyan-700"
                        >
                          Connect to IXP →
                        </button>
                      </div>
                    )
                  }
                />

              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MmrView;
